import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import type { PurchaseRequestDetailQuery } from 'generated/resources/graphql'
import {
  MarkUploadDocumentTaskAsDoneDocument,
  PurchaseRequestDetailDocument,
  PurchaseRequestFragment,
  RemoveUploadDocumentTaskFileDocument,
  UploadDocumentTaskFilesDocument,
} from 'generated/resources/graphql'

import DropZone from 'modules/core/components/DropZone'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropItem from 'modules/procurement/components/DropItem'

type ProcurementFile =
  | NonNullable<PurchaseRequestDetailQuery['procurement']['purchaseRequest']>['files'][number]
  | NonNullable<
      NonNullable<
        NonNullable<
          PurchaseRequestDetailQuery['procurement']['purchaseRequest']
        >['uploadDocumentTask']
      >[number]
    >['files'][number]

interface RequestDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  uploadDocumentTask: PurchaseRequestFragment['uploadDocumentTask'][0] | null | undefined
}

const RequestDocumentModal: React.FC<RequestDocumentModalProps> = ({
  isOpen,
  onClose,
  uploadDocumentTask,
}) => {
  const { company, employee } = useCurrentSession()

  const [uploadFiles, { loading: isUploading }] = useMutation(UploadDocumentTaskFilesDocument, {
    extractorFn: (data) => data.procurementMutations.uploadProcurementUploadDocumentTask,
    refetchQueries: [PurchaseRequestDetailDocument],
  })

  const [removeFile, { loading: isRemovingFile }] = useMutation(
    RemoveUploadDocumentTaskFileDocument,
    {
      extractorFn: (data) => data.procurementMutations.removeFileProcurementUploadDocumentTask,
      refetchQueries: [PurchaseRequestDetailDocument],
    }
  )

  const [markAsDone, { loading: isMarkingAsDone }] = useMutation(
    MarkUploadDocumentTaskAsDoneDocument,
    {
      extractorFn: (data) => data.procurementMutations.markAsDoneProcurementUploadDocumentTask,
      refetchQueries: [PurchaseRequestDetailDocument],
    }
  )

  const transformTaskableFileToProcurementFile = (file: {
    id: string
    filename: string
    url: string
    contentType?: string | null
    byteSize?: number | null
  }): ProcurementFile => {
    return {
      __typename: 'ProcurementFile',
      id: file.id,
      filename: file.filename,
      url: file.url,
      contentType: file.contentType || '',
      byteSize: file.byteSize || 0,
    }
  }

  const handleOnDrop = useUnsafeCallback(
    async (files: File[]) => {
      if (!uploadDocumentTask?.id || files.length === 0) return

      const result = await uploadFiles({
        variables: {
          files,
          uploadDocumentTaskId: uploadDocumentTask.id,
        },
      })

      const handled = handleResult(result, () => {})
      if (!handled.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'tasks.types.procurement_request_document.upload_document.errors.upload_error'
          ),
        })
      }
    },
    [uploadDocumentTask?.id, uploadFiles]
  )

  const handleFileRemove = useUnsafeCallback(
    async (fileId: string) => {
      if (!uploadDocumentTask?.id || !fileId) return

      const result = await removeFile({
        variables: {
          fileId: fileId,
          uploadDocumentTaskId: uploadDocumentTask.id,
        },
      })

      const handled = handleResult(result, () => {})
      if (!handled.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'tasks.types.procurement_request_document.upload_document.errors.remove_file_failed'
          ),
        })
      }
    },
    [uploadDocumentTask?.id, removeFile]
  )

  const handleConfirm = useUnsafeCallback(async () => {
    if (!uploadDocumentTask?.id || !employee?.id) return

    const hasFiles = uploadDocumentTask.files && uploadDocumentTask.files.length > 0
    if (!hasFiles) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.errors.file_not_attached'
        ),
      })
      return
    }

    const result = await markAsDone({
      variables: {
        uploadDocumentTaskId: uploadDocumentTask.id,
        completedById: employee.id,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.errors.mark_as_done_failed'
        ),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.success.mark_as_done'
        ),
      })
      onClose()
    }
  }, [uploadDocumentTask, employee?.id, markAsDone, onClose])

  if (!uploadDocumentTask) return null

  const hasFiles = uploadDocumentTask.files && uploadDocumentTask.files.length > 0
  const disableConfirm = !hasFiles || isMarkingAsDone

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.detail.request_document_modal.title')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.detail.request_document_modal.confirm'),
        onClick: handleConfirm,
        loading: isMarkingAsDone,
        disabled: disableConfirm,
      }}
    >
      <Box flexDirection="column" gap="s12">
        {uploadDocumentTask.taskContent && (
          <Box flexDirection="column" gap="s4">
            <RichTextDisplay
              content={uploadDocumentTask.taskContent}
              className="text-f1-foreground-secondary"
            />
          </Box>
        )}

        <DropZone
          onDrop={handleOnDrop}
          accept={{ 'application/pdf': [], 'image/jpeg': [], 'image/png': [] }}
          multiple
          loading={isUploading || isRemovingFile}
          placeholder={i18n.t('tasks.types.procurement_request_document.dropzone_placeholder')}
        />

        {hasFiles && (
          <Box flexDirection="column" gap="s8">
            {uploadDocumentTask.files.map((file) => {
              const procurementFile = transformTaskableFileToProcurementFile(file)
              return (
                <DropItem
                  key={file.id}
                  file={procurementFile}
                  onRemove={() => handleFileRemove(procurementFile.id)}
                />
              )
            })}
          </Box>
        )}
      </Box>
    </F0Dialog>
  )
}

export default RequestDocumentModal
